import React, { Component } from 'react';
import { Divider } from 'primereact/divider';
import { Splitter, SplitterPanel } from 'primereact/splitter';
import { ApiService } from 'hay-core-react';
import { UtilService } from 'hay-core-react';
import SocketService from '../../../service/SocketService';
import DashboardFiltros from './DashboardFiltros';
import DashboardStatCard from './DashboardStatCard';
import DashboardGraficoPizza from './DashboardGraficoPizza';
import DashboardGraficoHorarios from './DashboardGraficoHorarios';
import DashboardGraficoTrafego from './DashboardGraficoTrafego';
import DashboardTimeline from './DashboardTimeline';
import DashboardPeriodoEnum from '../../../enums/DashboardPeriodoEnum';

const PERIODO_CUSTOM = 'main.dashboard.periodo.periodo';
const TIPOLOG_CONEXAO = 'main.usuariologacesso.tipolog.conexao';
const TAMANHO_MAXIMO_TIMELINE = 30;

class DashboardTemplate extends Component {
    constructor(props) {
        super(props);
        this.state = {
            periodo: DashboardPeriodoEnum.getDefault().id,
            data: Date.now(),
            dataFim: null,
            routerboard: null,
            dados: null,
            carregando: true,
            buscando: false,
        };
    }

    componentDidMount() {
        this.buscarDados();
        this.iniciarSocket();
    }

    componentWillUnmount() {
        this.pararSocket();
    }

    // Conecta só enquanto o Dashboard está montado, e cria uma instância nova de socket a cada
    // montagem (nunca reaproveita uma anterior) — junto com a desconexão total em
    // componentWillUnmount, isso garante que sair/voltar da tela várias vezes nunca acumula
    // listeners nem duplica atualizações (ver especificação 118).
    iniciarSocket() {
        this.socket = SocketService.conectar();
        this.socket.on('connect', () => SocketService.subscribeDashboard(this.socket));
        this.socket.on('usuarioLogTempoReal', this.onEventoTempoReal);
    }

    pararSocket() {
        if (this.socket) {
            this.socket.off();
            this.socket.disconnect();
            this.socket = null;
        }
    }

    // Patch incremental do estado a partir de um evento de UsuarioLog em tempo real — nunca
    // refaz a busca completa (dashboard/find recomputa vários count/find + lookups assíncronos
    // por item, mais caro que necessário). O payload já vem com os campos resolvidos
    // (chaveDispositivo/chaveOs/chaveProvedor/hora) para não duplicar as regras de bucket/fuso
    // horário de UsuarioLog.dashboard() aqui (ver UsuarioLog.montarPayloadTempoReal).
    //
    // Usa a forma funcional de setState (lê `prevState` em vez de `this.state` capturado antes da
    // chamada) — obrigatório aqui: sob o automatic batching do React 18+ (createRoot, ver
    // src/index.js), duas chamadas de onEventoTempoReal disparadas em sequência rápida (ex.
    // desconexões em massa por "idle timeout", que chegam em rajada — visto em produção) podem cair
    // no mesmo batch. Lendo `this.state` diretamente, a segunda chamada calcularia em cima do
    // estado ANTIGO (o setState da primeira ainda não commitado), e o merge raso do setState faz a
    // última sobrescrever a primeira — perdendo o incremento de quantidade/bytesin/bytesout de um
    // dos dois eventos (reproduzido isoladamente antes deste fix). A forma funcional sempre
    // encadeia sobre o estado pendente mais recente, eliminando a corrida.
    onEventoTempoReal = (evento) => {
        this.setState((prevState) => {
            const { dados, routerboard } = prevState;
            if (!dados) return null;
            if (routerboard?.id && String(evento.routerboardId) !== String(routerboard.id)) return null;

            const ehConexao = evento.tipolog === TIPOLOG_CONEXAO;

            // usuariosOnline não é filtrado por período no backend (UsuarioLog.dashboard() consulta
            // UsuarioSessao.count sem filtro de data) — só o routerboard importa, já checado acima.
            const usuariosOnline = Math.max(0, dados.cards.usuariosOnline + (ehConexao ? 1 : -(evento.sessoesEncerradas || 0)));

            const dentroDoPeriodo = evento.datalog >= dados.periodoInicio && evento.datalog <= dados.periodoFim;
            if (!dentroDoPeriodo) {
                return { dados: { ...dados, cards: { ...dados.cards, usuariosOnline } } };
            }

            const loginsEfetuados = dados.cards.loginsEfetuados + (ehConexao ? 1 : 0);

            const incrementarBucket = (itens, chave) => {
                const existe = itens.some((item) => item.chave === chave);
                if (existe) return itens.map((item) => (item.chave === chave ? { ...item, quantidade: item.quantidade + 1 } : item));
                return [...itens, { chave, quantidade: 1 }];
            };
            const horarios = ehConexao
                ? dados.graficos.horarios.map((item) => (item.hora === evento.hora ? { ...item, quantidade: item.quantidade + 1 } : item))
                : dados.graficos.horarios;

            // Tráfego (bytesin/bytesout) só chega em eventos de desconexão (ver especificação 119) —
            // recalcula a média do bucket da hora incrementalmente (item.bytesin/bytesout já são
            // médias, não somas, então precisa "desfazer" a média antiga com item.quantidade antes de
            // somar o novo valor), mesmo raciocínio de incrementarBucket, sem reaproveitá-la porque o
            // valor agregado aqui não é uma contagem simples.
            const trafego = !ehConexao
                ? dados.graficos.trafego.map((item) => {
                    if (item.hora !== evento.hora) return item;
                    const quantidade = item.quantidade + 1;
                    return {
                        hora: item.hora,
                        quantidade,
                        bytesin: (item.bytesin * item.quantidade + evento.bytesin) / quantidade,
                        bytesout: (item.bytesout * item.quantidade + evento.bytesout) / quantidade,
                    };
                })
                : dados.graficos.trafego;

            const trafegoTotal = !ehConexao
                ? { bytesin: dados.cards.trafegoTotal.bytesin + evento.bytesin, bytesout: dados.cards.trafegoTotal.bytesout + evento.bytesout }
                : dados.cards.trafegoTotal;

            // Tempo médio de login (TML) só chega em eventos de conexão com medição válida (>0) —
            // ver especificação 121. Mesma técnica de média corrida do tráfego: usa
            // tempoMedioLoginQuantidade como peso da média antiga antes de somar o novo valor.
            const temMedicaoTempoLogin = ehConexao && evento.tempologin > 0;
            const tempoMedioLoginQuantidade = temMedicaoTempoLogin
                ? dados.cards.tempoMedioLoginQuantidade + 1
                : dados.cards.tempoMedioLoginQuantidade;
            const tempoMedioLoginMs = temMedicaoTempoLogin
                ? (dados.cards.tempoMedioLoginMs * dados.cards.tempoMedioLoginQuantidade + evento.tempologin) / tempoMedioLoginQuantidade
                : dados.cards.tempoMedioLoginMs;

            // Tempo médio de acesso (TMA) só chega em eventos de desconexão com uptime medido
            // (>0) — mede quanto tempo o usuário ficou de fato conectado, diferente do TML acima
            // (ver especificação 123). Mesma técnica de média corrida.
            const temMedicaoTempoAcesso = !ehConexao && evento.uptime > 0;
            const tempoMedioAcessoQuantidade = temMedicaoTempoAcesso
                ? dados.cards.tempoMedioAcessoQuantidade + 1
                : dados.cards.tempoMedioAcessoQuantidade;
            const tempoMedioAcessoMs = temMedicaoTempoAcesso
                ? (dados.cards.tempoMedioAcessoMs * dados.cards.tempoMedioAcessoQuantidade + evento.uptime * 1000) / tempoMedioAcessoQuantidade
                : dados.cards.tempoMedioAcessoMs;

            const novoItemTimeline = {
                id: evento.id,
                usuario: evento.usuario,
                routerboard: evento.routerboard,
                datalog: evento.datalog,
                tipolog: evento.tipolog,
                dispositivo: evento.dispositivo,
                os: evento.os,
                provedor: evento.provedor,
                mac: evento.mac,
                duracaoSessaoMs: evento.duracaoSessaoMs,
                numeroConexaoPeriodo: ehConexao ? loginsEfetuados : null,
                // Faltavam aqui — DashboardTimeline.content() só mostra os ícones de upload/download
                // quando item.bytesin/bytesout > 0; sem copiar do evento, o item entrava undefined e
                // os ícones nunca apareciam num log de desconexão adicionado via tempo real (só
                // reaparecendo depois de um refetch completo via dashboard/find).
                bytesin: evento.bytesin,
                bytesout: evento.bytesout,
            };

            return {
                dados: {
                    ...dados,
                    cards: {
                        ...dados.cards,
                        usuariosOnline,
                        loginsEfetuados,
                        trafegoTotal,
                        tempoMedioLoginMs,
                        tempoMedioLoginQuantidade,
                        tempoMedioAcessoMs,
                        tempoMedioAcessoQuantidade,
                    },
                    graficos: {
                        ...dados.graficos,
                        provedor: ehConexao ? incrementarBucket(dados.graficos.provedor, evento.chaveProvedor) : dados.graficos.provedor,
                        dispositivo: ehConexao ? incrementarBucket(dados.graficos.dispositivo, evento.chaveDispositivo) : dados.graficos.dispositivo,
                        os: ehConexao ? incrementarBucket(dados.graficos.os, evento.chaveOs) : dados.graficos.os,
                        horarios,
                        trafego,
                    },
                    timeline: [novoItemTimeline, ...dados.timeline].slice(0, TAMANHO_MAXIMO_TIMELINE),
                },
            };
        });
    }

    async buscarDados() {
        this.setState({ buscando: true });
        const { periodo, data, dataFim, routerboard } = this.state;
        const resultado = await ApiService.post('dashboard/find', {
            periodo,
            data,
            dataFim: periodo === PERIODO_CUSTOM ? dataFim : undefined,
            routerboardId: routerboard?.id,
        });
        this.setState({
            dados: resultado?.status ? resultado.data : this.state.dados,
            carregando: false,
            buscando: false,
        });
    }

    onChangePeriodo = (periodo) => {
        this.setState({ periodo, data: Date.now(), dataFim: null }, () => this.buscarDados());
    }

    onChangeData = (data) => {
        this.setState({ data }, () => this.buscarDados());
    }

    onChangeRange = (data, dataFim) => {
        this.setState({ data, dataFim }, () => {
            if (data && dataFim) this.buscarDados();
        });
    }

    onChangeRouterboard = (routerboard) => {
        this.setState({ routerboard }, () => this.buscarDados());
    }

    renderConteudo() {
        const { dados, buscando } = this.state;
        const cards = dados?.cards || {};
        const graficos = dados?.graficos || {};

        return (
            <div style={UtilService.reactEnabled(!buscando)}>
                <div className="mb-3">
                    <DashboardFiltros
                        periodo={this.state.periodo}
                        data={this.state.data}
                        dataFim={this.state.dataFim}
                        routerboard={this.state.routerboard}
                        onChangePeriodo={this.onChangePeriodo}
                        onChangeData={this.onChangeData}
                        onChangeRange={this.onChangeRange}
                        onChangeRouterboard={this.onChangeRouterboard}
                    />
                </div>

                <Splitter layout="horizontal" style={{ height: '75vh' }} className="border-round">
                    <SplitterPanel size={50} minSize={25} className="overflow-y-auto">
                        <div className="p-3">
                            <div className="grid">
                                <div className="col-12 md:col-6">
                                    <DashboardStatCard
                                        icone="pi-server"
                                        cor="blue"
                                        titulo={UtilService.traduzir('main.dashboard.card.routerboards')}
                                        valor={cards.routerboardsTotal ?? 0}
                                        subtitulo={`${cards.routerboardsAtivas ?? 0} ${UtilService.traduzir('main.dashboard.card.routerboards.ativas')}`}
                                        help={UtilService.traduzir('main.dashboard.card.routerboards.help')}
                                    />
                                </div>
                                <div className="col-12 md:col-6">
                                    <DashboardStatCard
                                        icone="pi-wifi"
                                        cor="green"
                                        titulo={UtilService.traduzir('main.dashboard.card.usuariosonline')}
                                        valor={cards.usuariosOnline ?? 0}
                                        help={UtilService.traduzir('main.dashboard.card.usuariosonline.help')}
                                    />
                                </div>
                                <div className="col-12 md:col-6">
                                    <DashboardStatCard
                                        icone="pi-sign-in"
                                        cor="orange"
                                        titulo={UtilService.traduzir('main.dashboard.card.loginsefetuados')}
                                        valor={cards.loginsEfetuados ?? 0}
                                        help={UtilService.traduzir('main.dashboard.card.loginsefetuados.help')}
                                    />
                                </div>
                                <div className="col-12 md:col-6">
                                    <DashboardStatCard
                                        icone="pi-user-plus"
                                        cor="purple"
                                        titulo={UtilService.traduzir('main.dashboard.card.cadastrosfeitos')}
                                        valor={cards.cadastrosFeitos ?? 0}
                                        help={UtilService.traduzir('main.dashboard.card.cadastrosfeitos.help')}
                                    />
                                </div>
                            </div>

                            <Divider align="left" type="solid">
                                <b>{UtilService.traduzir('main.dashboard.secao.acessos')}</b>
                            </Divider>

                            <div className="grid">
                                <div className="col-6" style={{ minWidth: 0 }}>
                                    <DashboardGraficoPizza titulo={UtilService.traduzir('main.dashboard.grafico.provedor')} itens={graficos.provedor} help={UtilService.traduzir('main.dashboard.grafico.provedor.help')} />
                                </div>
                                <div className="col-6" style={{ minWidth: 0 }}>
                                    <DashboardGraficoPizza titulo={UtilService.traduzir('main.dashboard.grafico.dispositivo')} itens={graficos.dispositivo} help={UtilService.traduzir('main.dashboard.grafico.dispositivo.help')} />
                                </div>
                                <div className="col-6" style={{ minWidth: 0 }}>
                                    <DashboardGraficoPizza titulo={UtilService.traduzir('main.dashboard.grafico.os')} itens={graficos.os} help={UtilService.traduzir('main.dashboard.grafico.os.help')} />
                                </div>
                                <div className="col-6" style={{ minWidth: 0 }}>
                                    <DashboardGraficoHorarios titulo={UtilService.traduzir('main.dashboard.grafico.horarios')} itens={graficos.horarios} help={UtilService.traduzir('main.dashboard.grafico.horarios.help')} />
                                </div>
                            </div>

                            <Divider align="left" type="solid">
                                <b>{UtilService.traduzir('main.dashboard.secao.trafego')}</b>
                            </Divider>

                            <div className="grid">
                                <div className="col-12 md:col-6">
                                    <DashboardStatCard
                                        icone="pi-arrow-right-arrow-left"
                                        cor="cyan"
                                        titulo={UtilService.traduzir('main.dashboard.card.trafegototal')}
                                        help={UtilService.traduzir('main.dashboard.card.trafegototal.help')}
                                        valor={(
                                            <div className="flex gap-3">
                                                <span className="text-green-500">
                                                    <i className="pi pi-cloud-upload mr-1" style={{ fontSize: '1rem' }} />
                                                    {UtilService.formatarBytesVolume(cards.trafegoTotal?.bytesin)}
                                                </span>
                                                <span className="text-blue-500">
                                                    <i className="pi pi-cloud-download mr-1" style={{ fontSize: '1rem' }} />
                                                    {UtilService.formatarBytesVolume(cards.trafegoTotal?.bytesout)}
                                                </span>
                                            </div>
                                        )}
                                    />
                                </div>
                                <div className="col-12 md:col-6 h-full">
                                    <div className="grid h-full">
                                        <div className="col-6 py-0" style={{ minWidth: 0 }}>
                                            <DashboardStatCard
                                                icone="pi-stopwatch"
                                                cor="indigo"
                                                titulo={UtilService.traduzir('main.dashboard.card.tempomediologin')}
                                                valor={cards.tempoMedioLoginQuantidade ? UtilService.formatarDuracaoCurta(cards.tempoMedioLoginMs) : '—'}
                                                help={UtilService.traduzir('main.dashboard.card.tempomediologin.help')}
                                            />
                                        </div>
                                        <div className="col-6 py-0" style={{ minWidth: 0 }}>
                                            <DashboardStatCard
                                                icone="pi-clock"
                                                cor="teal"
                                                titulo={UtilService.traduzir('main.dashboard.card.tempomedioacesso')}
                                                valor={cards.tempoMedioAcessoQuantidade ? UtilService.formatarDuracaoCurta(cards.tempoMedioAcessoMs) : '—'}
                                                help={UtilService.traduzir('main.dashboard.card.tempomedioacesso.help')}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12" style={{ minWidth: 0 }}>
                                    <DashboardGraficoTrafego titulo={UtilService.traduzir('main.dashboard.grafico.trafego')} itens={graficos.trafego} help={UtilService.traduzir('main.dashboard.grafico.trafego.help')} />
                                </div>
                            </div>
                        </div>
                    </SplitterPanel>

                    <SplitterPanel size={50} minSize={25} className="overflow-y-auto">
                        <div className="p-3">
                            <Divider align="left" type="solid">
                                <b>{UtilService.traduzir('main.dashboard.secao.timeline')}</b>
                            </Divider>
                            <DashboardTimeline itens={dados?.timeline} />
                        </div>
                    </SplitterPanel>
                </Splitter>
            </div>
        );
    }

    render() {
        if (this.state.carregando) {
            return (
                <div className="surface-card border-round shadow-2 h-full flex justify-content-center align-items-center">
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
                </div>
            );
        }

        return (
            <div className="surface-card border-round shadow-2 h-full p-3">
                {this.renderConteudo()}
            </div>
        );
    }
}

export default DashboardTemplate;
